import { useState, useEffect, useRef } from 'react';
import { SCREENS_PX } from '../../constants';

export const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(windowWidth < SCREENS_PX.sm);
  const [isTablet, setIsTablet] = useState(
    windowWidth >= SCREENS_PX.sm && windowWidth < SCREENS_PX.lg,
  );
  const [isDesktop, setIsDesktop] = useState(windowWidth >= SCREENS_PX.lg);

  const previousStatesRef = useRef({
    isMobile,
    isTablet,
    isDesktop,
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleResize = () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout
      timeoutRef.current = setTimeout(() => {
        const newWidth = window.innerWidth;
        setWindowWidth(newWidth);

        const newStates = {
          isMobile: newWidth < SCREENS_PX.sm,
          isTablet: newWidth >= SCREENS_PX.sm && newWidth < SCREENS_PX.lg,
          isDesktop: newWidth >= SCREENS_PX.lg,
        };

        // Only update states that have changed
        if (newStates.isMobile !== previousStatesRef.current.isMobile) {
          setIsMobile(newStates.isMobile);
          previousStatesRef.current.isMobile = newStates.isMobile;
        }

        if (newStates.isTablet !== previousStatesRef.current.isTablet) {
          setIsTablet(newStates.isTablet);
          previousStatesRef.current.isTablet = newStates.isTablet;
        }

        if (newStates.isDesktop !== previousStatesRef.current.isDesktop) {
          setIsDesktop(newStates.isDesktop);
          previousStatesRef.current.isDesktop = newStates.isDesktop;
        }
      }, 200); // 150ms delay
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      // Clear timeout on cleanup
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    windowWidth,
    isMobile,
    isTablet,
    isDesktop,
  };
};
